/**
 * Round 218A Phase 8D-2 (2026-02-05) — Business Credits balance hook.
 *
 * Fetches GET /api/billing/credits once and exposes a `refresh()`
 * imperative so callers can trigger a refetch after events that
 * change the balance (successful generation, refund, purchase,
 * monthly refill, completed video attempt).
 *
 * A module-level event bus lets ANY component in the tree trigger a
 * refresh — the ambient header pill listens without needing prop
 * drilling from the flow that just consumed / restored credits.
 *
 * Design notes:
 *   • Loading + error surfaces are non-blocking. If the API fails
 *     the pill simply hides (returns null); the Dashboard keeps
 *     rendering.
 *   • `isUnlimited` is authoritative — founders / internal accounts
 *     see "Unlimited" instead of a numeric balance.
 *   • Never displays per-asset credit costs. This module carries
 *     only the ambient balance + policy copy for the Settings
 *     section.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { businessCreditsAPI } from '@/utils/api';

// Module-level event bus for cross-component refresh triggers.
const REFRESH_EVENT = 'paygbot:business-credits-refresh';

export function notifyBusinessCreditsChanged() {
  try {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  } catch {
    /* SSR / non-browser — no-op */
  }
}

export default function useBusinessCredits({ auto = true } = {}) {
  const [state, setState] = useState({
    loading: auto,
    error: null,
    data: null,
  });
  const mountedRef = useRef(true);

  const fetchBalance = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const { data } = await businessCreditsAPI.getBalance();
      if (!mountedRef.current) return;
      setState({ loading: false, error: null, data });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({ loading: false, error: err, data: null });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (auto) fetchBalance();
    const onRefresh = () => {
      if (mountedRef.current) fetchBalance();
    };
    window.addEventListener(REFRESH_EVENT, onRefresh);
    return () => {
      mountedRef.current = false;
      window.removeEventListener(REFRESH_EVENT, onRefresh);
    };
  }, [auto, fetchBalance]);

  return {
    loading: state.loading,
    error: state.error,
    balance: state.data,   // full payload from /billing/credits
    refresh: fetchBalance,
  };
}
